import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/useAuthStore.jsx';
import '@assets/css/auth/login.css'

export default function LoginSuccess() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuthStore();
    const [status, setStatus] = useState('로그인 처리 중...');
    const [error, setError] = useState(null);

    useEffect(() => {
        const processLoginResponse = async () => {
            try {
                // URL 파라미터 확인
                const searchParams = new URLSearchParams(location.search);

                // 에러 파라미터가 있는지 확인
                const errorParam = searchParams.get('error');
                const errorMessage = searchParams.get('message');

                if (errorParam) {
                    setError(errorMessage || '로그인 처리 중 오류가 발생했습니다.');
                    setTimeout(() => navigate(`/error?error=${errorParam}&message=${errorMessage}`), 1000);
                    return;
                }

                // 토큰 파라미터 확인
                const accessToken = searchParams.get('ab_at');
                const refreshToken = searchParams.get('ab_rt');

                // 토큰이 없는 경우
                if (!accessToken || !refreshToken) {
                    // API 응답에서 직접 정보를 가져와야 하는 상황일 수 있음
                    // response 파라미터를 확인
                    const responseParam = searchParams.get('response');

                    if (responseParam) {
                        try {
                            const response = JSON.parse(decodeURIComponent(responseParam));

                            if (!response.success) {
                                setError(response.message || '로그인에 실패했습니다.');
                                return;
                            }

                            // 응답에 data가 있고 토큰 정보가 포함되어 있는 경우
                            if (response.data && response.data.accessToken && response.data.refreshToken) {
                                processTokenData(response.data.accessToken, response.data.refreshToken);
                                return;
                            }
                        } catch (e) {
                            console.error('응답 데이터 파싱 오류:', e);
                        }
                    }

                    setError('로그인 정보가 없습니다. 다시 로그인해주세요.');
                    return;
                }

                // 토큰이 있는 경우 처리
                processTokenData(accessToken, refreshToken);
            } catch (error) {
                console.error('로그인 처리 오류:', error);
                setError('로그인 처리 중 예기치 않은 오류가 발생했습니다.');
            }
        };

        const processTokenData = (accessToken, refreshToken) => {
            try {
                // JWT 토큰 디코딩
                const tokenParts = accessToken.split('.');
                if (tokenParts.length !== 3) {
                    throw new Error('유효하지 않은 토큰 형식입니다.');
                }

                // Base64 디코딩 및 페이로드 추출
                const base64Url = tokenParts[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const jsonPayload = decodeURIComponent(
                    atob(base64)
                    .split('')
                    .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join('')
                );
                const payload = JSON.parse(jsonPayload);

                // 사용자 정보 추출
                const userData = {
                    userId: payload.userId,
                    email: payload.sub,
                    name: payload.name,
                    isApproved: payload.isApproved,
                    isActive: payload.isActive,
                    branchIds: payload.branchIds,
                    roles: payload.roles || []
                };

                // 토큰 저장 (액세스 토큰 및 리프레시 토큰 모두 저장)
                localStorage.setItem('ab_at', accessToken);
                localStorage.setItem('ab_rt', refreshToken);
                console.log('토큰이 저장되었습니다: 액세스 토큰 및 리프레시 토큰');

                // 인증 상태 업데이트
                login(userData, {
                    accessToken,
                    refreshToken
                });

                if( userData.isActive ) {
                    if( userData.isApproved ) {
                        // 성공 메시지 표시 후 메인 페이지로 이동
                        setStatus('로그인 성공! 메인 페이지로 이동합니다.');
                        setTimeout(() => navigate('/'), 1000);
                    } else {
                        // 가입 승인전! 메인 페이지로 이동합니다.
                        setStatus('가입 승인 전! 메인 페이지로 이동합니다.');
                        setTimeout(() => navigate('/loginPendingApproval'), 1000);
                    }
                } else {
                    // 비활성화
                    setStatus('비활성화된 계정입니다.');
                    setTimeout(() => navigate('/loginFailure'), 1000);
                }
            } catch (error) {
                console.error('토큰 처리 오류:', error);
                setError('토큰 처리 중 오류가 발생했습니다.');
            }
        };

        processLoginResponse();
    }, [login, navigate, location]);

    // 오류가 있는 경우 로그인 페이지로 이동하는 함수
    const goToLogin = () => {
        navigate('/login');
    };

    // 오류가 있는 경우 오류 화면 표시
    if (error) {
        return (
            <div className="login-failure-container">
                <div className="login-failure-card">
                    <h2>로그인 실패</h2>
                    <div className="error-message">
                        <p>{error}</p>
                    </div>
                    <button onClick={goToLogin} className="login-button">
                        로그인 페이지로 이동
                    </button>
                </div>
            </div>
        );
    }

    // 정상 처리 중인 경우 로딩 화면 표시
    return (
        <div className="login-success-container">
            <div className="login-success-card">
                <h2>로그인</h2>
                <div className="status-message">
                    <p>{status}</p>
                </div>
                <div className="loading-spinner"></div>
            </div>
        </div>
    );
}